import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { projectsData } from "../data/projects.generated";
import { AuroraForecasterRadial } from "../components/AuroraForecasterRadial";
import { MetroinfoNanoleaf } from "../components/MetroinfoNanoleaf";

function measureSectionScrollBaseline(section: HTMLElement): number {
  return -section.getBoundingClientRect().top;
}

function getSectionScrollProgress(
  section: HTMLElement,
  viewport: number,
  baseline: number
): number {
  const rect = section.getBoundingClientRect();
  const range = section.offsetHeight - viewport;
  if (range <= 0) return rect.top <= 0 ? 1 : 0;
  const scrolled = -rect.top - baseline;
  return Math.max(0, Math.min(1, scrolled / range));
}

export function ProjectPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: "home" | "projects" } | null)?.from;
  const project = projectsData.find((p) => p.id === id) ?? null;
  const lenisRef = useRef<Lenis | null>(null);
  const metroSectionRef = useRef<HTMLElement>(null);
  const auroraSectionRef = useRef<HTMLElement>(null);
  const metroScrollBaseline = useRef(0);
  const auroraScrollBaseline = useRef(0);
  const [scrollY, setScrollY] = useState(0);
  const [metroSectionProgress, setMetroSectionProgress] = useState(0);
  const [auroraSectionProgress, setAuroraSectionProgress] = useState(0);

  const handleBack = () => {
    if (from === "projects") {
      navigate("/projects");
    } else {
      navigate("/#projects");
    }
  };

  useLayoutEffect(() => {
    const prevRestoration = history.scrollRestoration;
    history.scrollRestoration = "manual";

    const resetToTop = () => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      lenisRef.current?.scrollTo(0, { immediate: true });
    };

    resetToTop();
    const rafId = requestAnimationFrame(resetToTop);

    return () => {
      cancelAnimationFrame(rafId);
      history.scrollRestoration = prevRestoration;
    };
  }, [id]);

  useLayoutEffect(() => {
    const measureBaselines = () => {
      if (project?.id === "metroinfo-ha" && metroSectionRef.current) {
        metroScrollBaseline.current = measureSectionScrollBaseline(metroSectionRef.current);
        setMetroSectionProgress(0);
      }
      if (project?.id === "aurora-forecaster" && auroraSectionRef.current) {
        auroraScrollBaseline.current = measureSectionScrollBaseline(auroraSectionRef.current);
        setAuroraSectionProgress(0);
      }
    };

    measureBaselines();
    const rafId = requestAnimationFrame(measureBaselines);

    const observer = new ResizeObserver(measureBaselines);
    if (metroSectionRef.current) observer.observe(metroSectionRef.current);
    if (auroraSectionRef.current) observer.observe(auroraSectionRef.current);

    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [id, project?.id]);

  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });
    lenisRef.current = lenis;
    lenis.scrollTo(0, { immediate: true });

    const viewport = window.innerHeight;
    lenis.on("scroll", (e: { scroll: number }) => {
      setScrollY(e.scroll);
      if (project?.id === "metroinfo-ha") {
        const section = metroSectionRef.current;
        if (section) {
          setMetroSectionProgress(
            getSectionScrollProgress(section, viewport, metroScrollBaseline.current)
          );
        }
      }
      if (project?.id === "aurora-forecaster") {
        const section = auroraSectionRef.current;
        if (section) {
          setAuroraSectionProgress(
            getSectionScrollProgress(section, viewport, auroraScrollBaseline.current)
          );
        }
      }
    });

    function raf(time: number) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
      lenisRef.current = null;
    };
  }, [id, project?.id]);

  if (!project) {
    return (
      <div className="app">
        <div className="bg-layer" aria-hidden="true" />
        <main id="main-content" tabIndex={-1} className="full-viewport safe-gutter screen-stage flex flex-col items-center justify-center">
          <p className="text-text-dim mb-4">Project not found</p>
          <Link to="/projects" className="font-mono text-sm text-accent hover:underline">
            ← Back to projects
          </Link>
        </main>
      </div>
    );
  }

  const isMetroinfo = project.id === "metroinfo-ha";
  const isAuroraForecaster = project.id === "aurora-forecaster";

  return (
    <div className="app">
      <div className="bg-layer" aria-hidden="true" />
      <main id="main-content" tabIndex={-1}>
        <div className="max-w-[720px] md:max-w-[960px] mx-auto safe-gutter safe-top-gap pb-safe">
          {isMetroinfo ? (
            <section ref={metroSectionRef} className="min-h-[115vh] p-0 -mx-4 sm:-mx-6 md:-mx-8 -mt-16">
              <div className="sticky top-0 z-10 flex flex-col justify-start pb-6 pt-[var(--top-chrome-space)]">
                <button
                  type="button"
                  onClick={handleBack}
                  className="inline-flex items-center gap-2 font-mono text-sm text-accent hover:underline mb-8 bg-transparent border-none cursor-pointer p-0 self-start"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M19 12H5M12 19l-7-7 7-7" />
                  </svg>
                  {from === "projects" ? "Back to projects" : "Back to home"}
                </button>
                <header className="mb-8">
                  <span className="section-kicker font-mono text-xs text-accent mb-4 block">{project.year}</span>
                  <h1 className="text-[clamp(1.5rem,4vw,2.5rem)] mb-1">{project.title}</h1>
                  <p className="text-base text-text-dim mb-4">{project.subtitle}</p>
                  <div className="flex flex-wrap gap-4">
                    {project.tags.map((tag) => (
                      <span key={tag} className="font-mono text-xs px-2.5 py-1 border border-[var(--color-border)] text-text-muted">
                        {tag}
                      </span>
                    ))}
                  </div>
                </header>
                <MetroinfoNanoleaf scrollProgress={metroSectionProgress} />
              </div>
            </section>
          ) : isAuroraForecaster ? (
            <section ref={auroraSectionRef} className="min-h-[115vh] p-0 -mx-4 sm:-mx-6 md:-mx-8 -mt-16">
              <div className="sticky top-0 z-10 flex flex-col justify-start pb-6 pt-[var(--top-chrome-space)]">
                <button
                  type="button"
                  onClick={handleBack}
                  className="inline-flex items-center gap-2 font-mono text-sm text-accent hover:underline mb-8 bg-transparent border-none cursor-pointer p-0 self-start"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M19 12H5M12 19l-7-7 7-7" />
                  </svg>
                  {from === "projects" ? "Back to projects" : "Back to home"}
                </button>
                <header className="mb-8">
                  <span className="section-kicker font-mono text-xs text-accent mb-4 block">{project.year}</span>
                  <h1 className="text-[clamp(1.5rem,4vw,2.5rem)] mb-1">{project.title}</h1>
                  <p className="text-base text-text-dim mb-4">{project.subtitle}</p>
                  <div className="flex flex-wrap gap-4">
                    {project.tags.map((tag) => (
                      <span key={tag} className="font-mono text-xs px-2.5 py-1 border border-[var(--color-border)] text-text-muted">
                        {tag}
                      </span>
                    ))}
                  </div>
                </header>
                <AuroraForecasterRadial scrollProgress={auroraSectionProgress} />
              </div>
            </section>
          ) : (
            <>
              <button
                type="button"
                onClick={handleBack}
                className="inline-flex items-center gap-2 font-mono text-sm text-accent hover:underline mb-8 bg-transparent border-none cursor-pointer p-0"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M19 12H5M12 19l-7-7 7-7" />
                </svg>
                {from === "projects" ? "Back to projects" : "Back to home"}
              </button>
              <header className="mb-8">
                <span className="section-kicker font-mono text-xs text-accent mb-4 block">{project.year}</span>
                <h1 className="text-[clamp(1.5rem,4vw,2.5rem)] mb-1">{project.title}</h1>
                <p className="text-base text-text-dim mb-4">{project.subtitle}</p>
                <div className="flex flex-wrap gap-4">
                  {project.tags.map((tag) => (
                    <span key={tag} className="font-mono text-xs px-2.5 py-1 border border-[var(--color-border)] text-text-muted">
                      {tag}
                    </span>
                  ))}
                </div>
              </header>
            </>
          )}
          <div
            className={`project-detail-body text-[0.95rem] text-text-dim leading-relaxed [&_h2]:text-xl [&_h2]:mt-8 [&_h2]:mb-4 [&_h3]:text-[1.05rem] [&_h3]:mt-6 [&_h3]:mb-4 [&_p]:mb-4 [&_ul]:mb-4 [&_ol]:mb-4 [&_ul]:pl-8 [&_ol]:pl-8 [&_table]:w-full [&_table]:border-collapse [&_table]:my-4 [&_th]:p-4 [&_td]:p-4 [&_th]:border [&_td]:border [&_th]:border-[var(--color-border)] [&_td]:border-[var(--color-border)] [&_th]:text-left [&_td]:text-left [&_th]:font-mono [&_th]:text-accent ${isAuroraForecaster || isMetroinfo ? "-mt-2 [&_h2:first-child]:mt-4" : ""}`}
            dangerouslySetInnerHTML={{ __html: project.bodyHtml ?? "" }}
          />
        </div>
      </main>
    </div>
  );
}
